import { PublicLayout } from '@/components/PublicLayout';
import { Alert, LogoMark, SectionEyebrow } from '@/components/ui';
import { LegalSection, LegalBlock, LegalLink } from './MentionsLegalesPage';
import { Accessibility, AlertTriangle, FileText } from 'lucide-react';

/**
 * Déclaration d'accessibilité — obligatoire pour tout service numérique public,
 * conformément à l'article 47 de la loi n°&nbsp;2005-102 du 11 février 2005 et au
 * décret n°&nbsp;2019-768 du 24 juillet 2019 (transposition de la directive
 * européenne 2016/2102).
 *
 * Référentiel applicable&nbsp;: RGAA 4.1.2.
 *
 * État de conformité&nbsp;: "Partiellement conforme" en Livraison 1 — l'audit
 * complet aura lieu en Livraison 5 (pré-production).
 *
 * Refonte R12 (mai 2026) :
 *   - Header glass avec LogoMark Accessibility accent tech + eyebrow + titre Geist
 *   - Alert variant="warning" + icon AlertTriangle (au lieu du bloc warning custom border-l-4)
 *   - 6 sections numérotées avec LegalSection helper (importé de MentionsLegalesPage)
 *   - Bloc référent en LegalBlock glass
 *   - Liens externes defenseurdesdroits en LegalLink external
 */
export function AccessibilitePage() {
  return (
    <PublicLayout maxWidth="lg" centered={false}>
      <article className="space-y-6 py-4 animate-fade-in">
        {/* Header */}
        <header className="flex items-start gap-3 pb-4 border-b border-border">
          <LogoMark icon={Accessibility} accent="tech" size="lg" />
          <div className="flex-1">
            <SectionEyebrow as="div">Conformité RGAA 4.1.2</SectionEyebrow>
            <h1 className="mt-1 font-display text-3xl sm:text-4xl font-semibold tracking-r-tight text-ink">
              Déclaration d'accessibilité
            </h1>
            <p className="mt-2 text-sm text-ink-subtle leading-relaxed">
              Conformément à l'article 47 de la loi du 11 février 2005 et au décret
              n°&nbsp;2019-768 du 24 juillet 2019.
            </p>
          </div>
        </header>

        {/* État de conformité — Alert R2 */}
        <Alert variant="warning" icon={AlertTriangle}>
          <div className="space-y-1.5 leading-relaxed">
            <p>
              <strong className="font-semibold">Vision Pro Collectivité</strong> est{' '}
              <strong>partiellement conforme</strong> avec le Référentiel Général d'Amélioration
              de l'Accessibilité (RGAA), <strong>version 4.1.2</strong>, en raison des
              non-conformités identifiées ci-dessous.
            </p>
          </div>
        </Alert>

        <p className="text-sm text-ink-muted leading-relaxed">
          Le Service est en cours de développement progressif. La présente déclaration
          correspond à la <strong className="text-ink">Livraison 1 (Passe 2A.1)</strong> du{' '}
          1.0. Un audit RGAA complet réalisé par un tiers
          indépendant interviendra avant la mise en production publique, en{' '}
          <strong className="text-ink">Livraison 5</strong> (pré-production), conformément à la
          roadmap projet.
        </p>

        {/* ============ Résultats des tests ============ */}
        <LegalSection number="1" title="Résultats des tests">
          <p className="text-sm text-ink-muted leading-relaxed">
            Auto-évaluation interne portant sur les pages publiques (accueil, connexion,
            inscription, mot de passe oublié, vérification d'email, pages légales) <strong className="text-ink">et
            sur les pages applicatives</strong> (espace citoyen, modules mairie&nbsp;: GRC,
            urbanisme, scolaire, associations, service technique). L'auto-évaluation porte sur
            les 13 thématiques et 106 critères du RGAA 4.1.2&nbsp;:
          </p>
          <ul className="list-disc pl-6 space-y-1.5 text-sm text-ink-muted leading-relaxed marker:text-ink-subtle">
            <li>
              <strong className="text-ink">Conformes</strong>&nbsp;: structure sémantique HTML5
              (header, main, nav, footer), titres hiérarchisés (h1 → h6), <strong className="text-ink">titre
              de page unique par écran</strong> (SC 2.4.2), labels explicites sur tous les
              formulaires, focus visible (anneau 2px), <strong className="text-ink">skip link
              «&nbsp;Aller au contenu principal&nbsp;» opérationnel sur toutes les pages</strong>,
              contrastes texte ≥ 4.5:1 en modes clair et sombre, touch targets ≥ 44×44 px
              (WCAG 2.2 SC 2.5.8), attribut <code className="rounded-r-md bg-surface/60 px-1 py-0.5 font-mono text-xs">lang="fr"</code>,
              support clavier complet, boîtes de dialogue accessibles
              (<code className="rounded-r-md bg-surface/60 px-1 py-0.5 font-mono text-xs">role="dialog"</code>,
              <code className="rounded-r-md bg-surface/60 px-1 py-0.5 font-mono text-xs">aria-modal</code>,
              fermeture par Échap), support du mode sombre, alertes annoncées
              (<code className="rounded-r-md bg-surface/60 px-1 py-0.5 font-mono text-xs">role="alert"</code>) ;
            </li>
            <li>
              <strong className="text-ink">À auditer par un tiers</strong>&nbsp;: tests avec
              lecteurs d'écran réels (NVDA, JAWS, VoiceOver), navigation mobile en haut
              contraste, compatibilité zoom 200&nbsp;%, sous-titres et transcriptions des
              contenus multimédias (aucun contenu multimédia à ce stade, donc non applicable) ;
            </li>
            <li>
              <strong className="text-ink">Limitations connues</strong>&nbsp;: certaines couleurs
              de texte très secondaire (états désactivés, mentions discrètes) peuvent, dans de
              rares cas, rester sous le seuil AA sur fond coloré&nbsp;; ces cas sont en cours de
              correction. Un audit RGAA complet par un tiers indépendant est prévu avant la mise
              en production publique généralisée.
            </li>
          </ul>
        </LegalSection>

        {/* ============ Méthodologie ============ */}
        <LegalSection number="2" title="Méthodologie d'évaluation">
          <p className="text-sm text-ink-muted leading-relaxed">
            L'auto-évaluation a été menée avec les outils suivants&nbsp;:
          </p>
          <ul className="list-disc pl-6 space-y-1 text-sm text-ink-muted leading-relaxed marker:text-ink-subtle">
            <li>Lighthouse (Chrome DevTools) — audits Accessibility &gt; 90 sur les pages L1 ;</li>
            <li>Axe DevTools — détection automatique des violations WCAG 2.1 AA ;</li>
            <li>Tests manuels au clavier (Tab, Shift+Tab, Entrée, Échap) ;</li>
            <li>Contrôle visuel des contrastes avec un outil de simulation daltonienne.</li>
          </ul>
          <p className="text-sm text-ink-muted leading-relaxed">
            L'audit externe de Livraison 5 sera réalisé selon la <strong className="text-ink">méthodologie RGAA
            4.1.2</strong>, échantillon complet du Service (pages publiques + 50&nbsp;% des
            pages applicatives), test avec outil d'assistance et user testing.
          </p>
        </LegalSection>

        {/* ============ Contenus non accessibles ============ */}
        <LegalSection number="3" title="Contenus non accessibles">
          <p className="text-sm text-ink-muted leading-relaxed">
            Aucun contenu identifié comme non-accessible en L1 sur les pages publiques. Les
            pages applicatives non encore livrées feront l'objet d'une déclaration complétée à
            mesure des livraisons.
          </p>
          <p className="text-sm text-ink-muted leading-relaxed">
            <strong className="text-ink">Dérogations pour charge disproportionnée&nbsp;:</strong>{' '}
            aucune à ce jour.
          </p>
          <p className="text-sm text-ink-muted leading-relaxed">
            <strong className="text-ink">Contenus hors obligation légale d'accessibilité&nbsp;:</strong>{' '}
            aucun à ce jour.
          </p>
        </LegalSection>

        {/* ============ Voies de recours ============ */}
        <LegalSection number="4" title="Voies de recours et contact">
          <p className="text-sm text-ink-muted leading-relaxed">
            Si vous constatez un problème d'accessibilité sur le Service, vous pouvez nous
            contacter pour obtenir le contenu sous une forme accessible ou pour signaler la
            difficulté&nbsp;:
          </p>
          <LegalBlock>
            <div>
              <strong className="text-ink">Référent accessibilité&nbsp;:</strong>{' '}
              One For All Distribution
            </div>
            <div className="mt-1">
              Email&nbsp;:{' '}
              <LegalLink href="mailto:contact@oneforall-distribution.fr">
                contact@oneforall-distribution.fr
              </LegalLink>
            </div>
            <div className="mt-1">Adresse postale&nbsp;: 6 bis place du Bastion, 17100 Saintes</div>
          </LegalBlock>
          <p className="text-sm text-ink-muted leading-relaxed">
            <strong className="text-ink">Si vous n'obtenez pas de réponse</strong> de notre part
            dans un délai de deux mois, vous pouvez saisir gratuitement le Défenseur des droits.
            Plusieurs moyens sont à votre disposition&nbsp;:
          </p>
          <ul className="list-disc pl-6 space-y-1.5 text-sm text-ink-muted leading-relaxed marker:text-ink-subtle">
            <li>
              Formulaire en ligne&nbsp;:{' '}
              <LegalLink href="https://formulaire.defenseurdesdroits.fr/" external>
                formulaire.defenseurdesdroits.fr
              </LegalLink>
            </li>
            <li>
              Liste des délégués territoriaux et coordonnées par téléphone (numéro non
              surtaxé)&nbsp;:{' '}
              <LegalLink href="https://www.defenseurdesdroits.fr/saisir/delegues" external>
                defenseurdesdroits.fr/saisir/delegues
              </LegalLink>
            </li>
            <li>
              Courrier postal&nbsp;: Défenseur des droits, Libre réponse 71120, 75342 Paris
              CEDEX 07 (envoi gratuit, sans timbre).
            </li>
          </ul>
        </LegalSection>

        <footer className="pt-4 border-t border-border text-xs text-ink-subtle">
          <FileText className="mr-1 inline h-3.5 w-3.5 align-text-bottom" aria-hidden="true" />
          Établie le <strong className="text-ink">1.0</strong>. Cette
          déclaration sera mise à jour à chaque livraison majeure et après chaque audit externe.
        </footer>
      </article>
    </PublicLayout>
  );
}
